import * as React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
  Tooltip,
} from '@mui/material';
import { notifyCardStyles } from './styles';
import { Edit, Delete } from '@mui/icons-material';
import { NotesIcon, CompanyIcon, AddressIcon } from 'components/Shared/Icons/tooltip_icons';
import { Notify } from 'types/entities/notify_type';
import { useState } from 'react';
import axiosService from 'utils/axios';
import AddOrEditNotify from './AddOrEditNotify';
import CompanyCard from '../companies/CompanyCard';
import CardTitle from 'components/Shared/CardsView/CardTitle';
import ModalErrorPopup from '../../../components/Modal/ModalErrorPopup';
import DestinationCard from '../destinations/DestinationCard';
import { ErrorResponse } from '../../../types/general/types';

interface Props {
  option: Notify;
  showActionButtons: boolean;
  onNotifyDelete?: () => void;
  onNotifyUpdate?: () => void;
}

const NotifyCard = (props: Props) => {
  const { option: notify, showActionButtons = true, onNotifyDelete, onNotifyUpdate } = props;

  const [editNotifyModalOpen, setEditNotifyModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<ErrorResponse | null>();

  const deleteNotify = () => {
    axiosService
      .delete(`/entities/notify/${notify.id}/`)
      .then(() => {
        if (onNotifyDelete) {
          onNotifyDelete();
        }
      })
      .catch(error => setDeleteError(error))
      .finally(() => setDeleteDialogOpen(false));
  };

  const onNotifyEdit = () => {
    if (onNotifyUpdate) {
      onNotifyUpdate();
    }
    setEditNotifyModalOpen(false);
  };

  return (
    <>
      <Card elevation={1} sx={notifyCardStyles.optionCard}>
        <CardHeader
          action={
            showActionButtons ? (
              <>
                <Tooltip title="Edit">
                  <IconButton
                    sx={notifyCardStyles.cardAction}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setEditNotifyModalOpen(true);
                    }}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    sx={notifyCardStyles.cardAction}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              </>
            ) : null
          }
          title={<CardTitle title={notify.company.name} tooltipText={notify.id} />}
          subheader="Notify"
          sx={notifyCardStyles.cardHeader}
        />
        <CardContent>
          <Box sx={notifyCardStyles.contactBox}>
            <CompanyIcon />
            <Tooltip
              title={<CompanyCard option={notify.company} showActionButtons={false} />}
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: 'transparent',
                    maxWidth: 'none',
                  },
                },
              }}
            >
              <Typography color="text" sx={notifyCardStyles.contactValue}>
                {notify.company.name}
              </Typography>
            </Tooltip>
          </Box>
          {notify.destination ? (
            <Box sx={notifyCardStyles.contactBox}>
              <AddressIcon tooltipText="Destination" />
              <Tooltip
                title={<DestinationCard option={notify.destination} showActionButtons={false} />}
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: 'transparent',
                      maxWidth: 'none',
                    },
                  },
                }}
              >
                <Typography color="text" sx={notifyCardStyles.contactValue}>
                  {notify.destination.name}
                </Typography>
              </Tooltip>
            </Box>
          ) : null}
          <Box sx={notifyCardStyles.contactBox}>
            <NotesIcon />
            <Typography color="text" sx={notifyCardStyles.contactValue}>
              {notify.notes || '-'}
            </Typography>
          </Box>
        </CardContent>
      </Card>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle id="alert-dialog-title">Delete notify</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete notify <b>{notify.company.name}</b>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={deleteNotify} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <AddOrEditNotify
        notify={notify}
        onSave={onNotifyEdit}
        modalTitle="Edit notify"
        isModalOpen={editNotifyModalOpen}
        setModalOpen={setEditNotifyModalOpen}
      />
      {/* @ts-ignore:next-line*/}
      <ModalErrorPopup
        title={deleteError?.statusText}
        message={deleteError?.message}
        onClose={() => setDeleteError(null)}
      />
    </>
  );
};

export default NotifyCard;
